import { computed, type Ref, type ComputedRef } from 'virtual-vue';
import type { PickerOption } from '../picker/types';
import type { DatePickerColumnType, DatePickerFormatter, DatePickerFilter } from './types';

// ==================== 工具函数 ====================

/** 获取指定年月的天数，利用 Date(year, month, 0) 取上月最后一天 */
function getMonthDays(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

/** 生成 [start, end] 闭区间的整数序列 */
function range(start: number, end: number): number[] {
  const result: number[] = [];
  for (let i = start; i <= end; i++) {
    result.push(i);
  }
  return result;
}

/** 数字转两位零填充字符串（1 → '01'） */
function padZero(num: number): string {
  return String(num).padStart(2, '0');
}

// ==================== 默认格式化 ====================

/** 默认 formatter：在数值后追加中文单位（年/月/日） */
const defaultFormatter: DatePickerFormatter = (type, option) => {
  const suffixMap: Record<DatePickerColumnType, string> = {
    year: '年',
    month: '月',
    day: '日',
  };
  return {
    ...option,
    text: `${option.value}${suffixMap[type] || ''}`,
  };
};

// ==================== 类型定义 ====================

interface UseDateColumnsOptions {
  /** 列类型数组，决定展示哪些列及顺序，如 ['year', 'month', 'day'] */
  columnsType: Ref<DatePickerColumnType[]> | ComputedRef<DatePickerColumnType[]>;
  /** 可选范围的最小日期 */
  minDate: Ref<Date> | ComputedRef<Date>;
  /** 可选范围的最大日期 */
  maxDate: Ref<Date> | ComputedRef<Date>;
  /** 当前选中值数组，与列类型一一对应（如 ['2024', '06', '15']） */
  currentValues: Ref<string[]>;
  /** 自定义格式化函数，可修改选项的显示文本 */
  formatter: Ref<DatePickerFormatter | undefined> | ComputedRef<DatePickerFormatter | undefined>;
  /** 自定义过滤函数，可移除不需要的选项（如仅保留季度月份） */
  filter: Ref<DatePickerFilter | undefined> | ComputedRef<DatePickerFilter | undefined>;
}

// ==================== 核心 composable ====================

/**
 * DatePicker 的列数据生成 composable。
 *
 * 职责：
 * 1. 根据 columnsType / minDate / maxDate / currentValues 动态计算每一列的选项数组
 * 2. 提供 correctValues 用于将越界值修正到合法范围
 *
 * 列选项范围模型：
 *   - year 列：[minDate.year, maxDate.year]
 *   - month 列：当年等于 minYear 时从 minMonth 起，等于 maxYear 时到 maxMonth 止，否则 [1, 12]
 *   - day 列：同理受 min/max 和当月天数约束
 */
export function useDateColumns(options: UseDateColumnsOptions) {
  const { currentValues } = options;

  // ==================== 当前选中值解析 ====================

  /**
   * 从 currentValues 中按 columnsType 的索引位置提取年/月/日数值。
   * 用于 columns computed 和 generateColumnOptions 中确定依赖列的约束条件。
   */
  const parsedValues = computed(() => {
    const types = options.columnsType.value;
    const vals = currentValues.value;
    const yearIdx = types.indexOf('year');
    const monthIdx = types.indexOf('month');
    const dayIdx = types.indexOf('day');

    const year = yearIdx >= 0 && vals[yearIdx] ? Number(vals[yearIdx]) : new Date().getFullYear();
    const month = monthIdx >= 0 && vals[monthIdx] ? Number(vals[monthIdx]) : 1;
    const day = dayIdx >= 0 && vals[dayIdx] ? Number(vals[dayIdx]) : 1;
    return { year, month, day };
  });

  // ==================== 单列选项生成 ====================

  interface GenerateColumnOptionsParams {
    type: DatePickerColumnType;
    min: Date;
    max: Date;
    curYear: number;
    curMonth: number;
    formatter: DatePickerFormatter;
    filter: DatePickerFilter | undefined;
  }

  /**
   * 根据列类型和当前选中的年/月，生成该列的所有可选项。
   *
   * 范围约束规则：
   * - year：始终为 [minYear, maxYear]
   * - month：curYear == minYear 时起始受 minMonth 约束；curYear == maxYear 时终止受 maxMonth 约束
   * - day：除 min/max 边界约束外，还受当月实际天数限制
   *
   * 生成后依次经过 formatter（格式化文本）和 filter（过滤选项）处理。
   */
  function generateColumnOptions(params: GenerateColumnOptionsParams): PickerOption[] {
    const { type, min, max, curYear, curMonth, formatter, filter } = params;
    const minYear = min.getFullYear();
    const maxYear = max.getFullYear();
    const minMonth = min.getMonth() + 1;
    const maxMonth = max.getMonth() + 1;
    const minDay = min.getDate();
    const maxDay = max.getDate();

    let rangeValues: number[];

    if (type === 'year') {
      rangeValues = range(minYear, maxYear);
    } else if (type === 'month') {
      const startMonth = curYear === minYear ? minMonth : 1;
      const endMonth = curYear === maxYear ? maxMonth : 12;
      rangeValues = range(
        Math.max(1, startMonth),
        Math.min(12, endMonth),
      );
    } else {
      const daysInMonth = getMonthDays(curYear, curMonth);
      const startDay = (curYear === minYear && curMonth === minMonth) ? minDay : 1;
      const endDay = (curYear === maxYear && curMonth === maxMonth)
        ? Math.min(maxDay, daysInMonth)
        : daysInMonth;
      rangeValues = range(
        Math.max(1, startDay),
        Math.min(daysInMonth, endDay),
      );
    }

    let opts: PickerOption[] = rangeValues.map((val) => {
      const strValue = type === 'year' ? String(val) : padZero(val);
      return formatter(type, { text: strValue, value: strValue });
    });

    if (filter) {
      opts = filter(type, opts);
    }

    return opts;
  }

  // ==================== 响应式列数据 ====================

  /**
   * 最终供 Picker 组件消费的二维列数据。
   * 依赖 currentValues 中的年/月值来动态计算月/日列的可选范围。
   */
  const columns = computed<PickerOption[][]>(() => {
    const types = options.columnsType.value;
    const min = options.minDate.value;
    const max = options.maxDate.value;
    const formatter = options.formatter.value || defaultFormatter;
    const filter = options.filter.value;

    const { year: curYear, month: curMonth } = parsedValues.value;

    return types.map(type => generateColumnOptions({ type, min, max, curYear, curMonth, formatter, filter }));
  });

  // ==================== 值修正 ====================

  /**
   * 将给定的值数组修正到合法范围内。
   *
   * 使用场景：
   * - 年份变化后，月/日可能越界（如从 maxYear 的 3 月切到非边界年，月仍合法；
   *   但从非边界年切到 minYear 时，早于 minMonth 的月份不再可选）
   * - minDate / maxDate 动态变化后，已选值可能不在新范围内
   *
   * 修正策略：逐列检查值是否在该列的合法选项中，
   * 若不存在则取数值上最接近的合法选项（而非简单取首/末项），以贴近用户原始意图。
   */
  const correctValues = (vals: string[]): string[] => {
    const types = options.columnsType.value;
    const min = options.minDate.value;
    const max = options.maxDate.value;
    const formatter = options.formatter.value || defaultFormatter;
    const filter = options.filter.value;

    const corrected = [...vals];

    const yearIdx = types.indexOf('year');
    const monthIdx = types.indexOf('month');

    const curYear = yearIdx >= 0 && corrected[yearIdx]
      ? Number(corrected[yearIdx])
      : new Date().getFullYear();
    const curMonth = monthIdx >= 0 && corrected[monthIdx]
      ? Number(corrected[monthIdx])
      : 1;

    types.forEach((type, idx) => {
      const effectiveYear = yearIdx >= 0 && corrected[yearIdx]
        ? Number(corrected[yearIdx])
        : curYear;
      const effectiveMonth = monthIdx >= 0 && corrected[monthIdx]
        ? Number(corrected[monthIdx])
        : curMonth;

      const col = generateColumnOptions({ type, min, max, curYear: effectiveYear, curMonth: effectiveMonth, formatter, filter });
      const exists = col.some(opt => String(opt.value) === corrected[idx]);
      if (!exists && col.length > 0) {
        const curNum = Number(corrected[idx]);
        const closest = col.reduce((prev, curr) => (Math.abs(Number(curr.value) - curNum) < Math.abs(Number(prev.value) - curNum)
          ? curr : prev));
        corrected[idx] = String(closest.value);
      }
    });

    return corrected;
  };

  return {
    columns,
    parsedValues,
    correctValues,
  };
}
